import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ShieldCheck, ChevronDown, ChevronRight, ExternalLink, Loader2, MapPin, X,
  FileText, Tag, BookOpen, CalendarDays, Fish, GraduationCap, Landmark, TreePine,
  Star, Crosshair, User, Map as MapIcon, Compass, Plus, Check, ArrowRight,
} from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { api, mediaSrc } from "@/lib/api";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { PolicyBar } from "@/components/PolicyBar";
import { StatePartnerContent } from "@/components/StatePartnerContent";
import { YourStatePanel } from "@/components/YourStatePanel";
import { DataHealthPanel } from "@/components/DataHealthPanel";
import { SpecialDrawsPanel } from "@/components/SpecialDrawsPanel";
import { SourceBadge } from "@/components/SourceBadge";
import { PartnerRecommendations } from "@/components/PartnerRecommendations";
import {
  SPECIES_PHOTO, SPECIES_PHOTO_FALLBACK, STATE_SPECIES, STATE_HERO, STATE_HERO_DEFAULT,
} from "@/lib/stateMeta";

// Resource-type -> friendly label + thin-line icon.
const RES = {
  agency: { label: "State wildlife agency", Icon: Landmark },
  hunting_license: { label: "Buy / apply — hunting license", Icon: FileText },
  licenses: { label: "Buy / apply — hunting license", Icon: FileText },
  tags_draws: { label: "Tags & draw applications", Icon: Tag },
  points: { label: "Preference points", Icon: Star },
  hunting_regs: { label: "Hunting regulations", Icon: BookOpen },
  seasons: { label: "Season dates", Icon: CalendarDays },
  fishing_license: { label: "Fishing licenses", Icon: Fish },
  fishing_regs: { label: "Fishing regulations", Icon: Fish },
  hunter_ed: { label: "Hunter education", Icon: GraduationCap },
  hunter_education: { label: "Hunter education", Icon: GraduationCap },
  conservation: { label: "Conservation & access programs", Icon: TreePine, photo: true },
  access: { label: "Conservation & access programs", Icon: TreePine, photo: true },
  gis: { label: "Hunt unit maps", Icon: MapIcon },
  harvest: { label: "Harvest reports", Icon: BookOpen },
};
const resMeta = (t) => RES[t] || { label: (t || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()), Icon: ExternalLink };
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : null);
const HUNT_TYPE = { general: "General", controlled: "Controlled", otc: "OTC" };
const DOC_CAT = { draw_odds: "Draw odds", point_creep: "Point creep", regulations: "Regulations", seasons: "Seasons", licenses: "Licenses", general: "General" };
const docSrc = (u) => mediaSrc(u);

export default function StatePage() {
  const { state } = useParams();
  // URL slugs arrive lowercase (e.g. /states/wyoming) — always show a proper-cased name
  const displayState = (state || "").replace(/[-_]/g, " ").replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [data, setData] = useState(null);
  const [resources, setResources] = useState([]);
  const [stateDocs, setStateDocs] = useState([]);
  const [allHunts, setAllHunts] = useState([]);
  const [hunts, setHunts] = useState([]);
  const [sel, setSel] = useState(null);
  const [oddsInfo, setOddsInfo] = useState(false);
  const [f, setF] = useState({ species: "", weapon: "", residency: "Nonresident", unit: "", season: "", points: "" });
  const { setAiContext, compareStates, toggleCompare } = useUI();
  const pinned = compareStates.includes(state);
  const pinState = () => {
    if (!pinned && compareStates.length >= 3) { return; }
    toggleCompare(state);
  };

  useEffect(() => {
    setAiContext({ state, species: f.species || undefined, unit: sel?.unit || undefined, weapon: f.weapon || undefined, page: "state" });
    return () => setAiContext(null);
  }, [state, f.species, f.weapon, sel, setAiContext]);

  useEffect(() => { api.exploreStates().then(setStates).catch(() => {}); }, []);
  useEffect(() => {
    setData(null); setSel(null); setF({ species: "", weapon: "", residency: "Nonresident", unit: "", season: "", points: "" });
    api.exploreSpecies(state).then(setData).catch(() => setData({ species: [], system: {} }));
    api.officialResources(state).then((d) => setResources(d || [])).catch(() => setResources([]));
    api.publicStateDocs(state).then((d) => setStateDocs(d || [])).catch(() => setStateDocs([]));
    api.exploreHunts(state).then((d) => { setAllHunts(d.hunts); setHunts(d.hunts); setSel(d.hunts[0] || null); }).catch(() => { setAllHunts([]); setHunts([]); });
  }, [state]);

  const meta = states.find((s) => s.state === state);
  const agency = meta?.agency;
  const dataSpecies = data?.species || [];
  const dataByName = useMemo(() => Object.fromEntries(dataSpecies.map((s) => [s.species, s])), [dataSpecies]);
  const railNames = useMemo(() => STATE_SPECIES[state] || dataSpecies.map((s) => s.species), [state, dataSpecies]);
  const weapons = dataByName[f.species]?.weapons || [];
  const units = useMemo(() => [...new Set(allHunts.map((h) => h.unit).filter(Boolean))], [allHunts]);
  const seasons = useMemo(() => [...new Set(allHunts.map((h) => h.season).filter(Boolean))], [allHunts]);
  const verifiedDate = useMemo(() => {
    const dates = resources.map((r) => r.last_checked_at).filter(Boolean).sort();
    return dates.length ? fmtDate(dates[dates.length - 1]) : null;
  }, [resources]);
  const heroImg = STATE_HERO[state] || STATE_HERO_DEFAULT;

  const findHunts = (overrides = {}) => {
    const eff = { ...f, ...overrides };
    const params = {};
    if (eff.species) params.species = eff.species;
    if (eff.weapon) params.weapon = eff.weapon;
    if (eff.residency) params.residency = eff.residency;
    if (eff.points !== "") params.points = Number(eff.points);
    api.exploreHunts(state, params).then((d) => {
      let rows = d.hunts;
      if (eff.unit) rows = rows.filter((h) => h.unit === eff.unit);
      if (eff.season) rows = rows.filter((h) => h.season === eff.season);
      setHunts(rows); setSel(rows[0] || null);
    }).catch(() => {});
  };

  const pickSpecies = (name) => {
    navigate(`/states/${encodeURIComponent(state)}/species/${encodeURIComponent(name)}`);
  };

  return (
    <div className="bg-paper min-h-screen" data-testid="state-page">
      <SiteHeader />

      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-4">
        <nav className="flex items-center gap-1.5 text-xs text-charcoal/50" data-testid="state-breadcrumb" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-blaze">Home</Link><ChevronRight size={13} />
          <Link to="/explore" className="hover:text-blaze">States</Link><ChevronRight size={13} />
          <span className="text-forest font-medium">{displayState}</span>
        </nav>
      </div>

      {/* Personalized "Your {state}" block for logged-in hunters */}
      <YourStatePanel state={state} />

      {/* Split hero */}
      <section className="relative bg-paper overflow-hidden" data-testid="state-hero">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-[1fr_1.05fr] items-stretch">
          <div className="px-5 sm:px-10 pt-6 pb-10 lg:py-14 flex flex-col justify-center relative z-10">
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-[5.2rem] leading-[0.88] tracking-tight text-charcoal" aria-label={`Plan your ${displayState} hunt.`}><span aria-hidden="true">Plan your<br />{displayState} hunt.</span></h1>
            <p className="text-base text-charcoal/65 mt-5 max-w-md leading-relaxed">Official resources, draw odds, regulations, and everything you need for a successful season.</p>
            <div className="mt-7">
              <div className="relative inline-block">
                <MapPin size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-blaze pointer-events-none" />
                <select value={state} onChange={(e) => navigate(`/states/${encodeURIComponent(e.target.value)}`)}
                  className="appearance-none bg-white border border-saddle/25 text-charcoal font-medium pl-10 pr-12 py-3 text-sm outline-none cursor-pointer hover:border-forest transition-colors min-w-[220px]" data-testid="state-select">
                  {states.map((s) => <option key={s.state} value={s.state}>{s.state}</option>)}
                </select>
                <ChevronDown size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-charcoal/60 pointer-events-none" />
              </div>
            </div>
            <span className="inline-flex items-center gap-2 text-sm text-charcoal/55 mt-4" data-testid="verified-line">
              <ShieldCheck size={16} className="text-[#2E7D46]" /> Information verified{verifiedDate ? ` ${verifiedDate}` : ""}
            </span>
            <div className="flex items-center gap-3 mt-5">
              <button onClick={pinState} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${pinned ? "bg-forest text-bone border-forest" : "bg-white text-charcoal border-saddle/30 hover:border-forest"}`} data-testid="state-compare-pin">
                {pinned ? <><Check size={15} /> Added to compare</> : <><Plus size={15} /> Add to compare</>}
              </button>
              {compareStates.length > 0 && (
                <Link to="/explore" className="inline-flex items-center gap-1 text-sm font-semibold text-blaze hover:underline" data-testid="state-compare-link">Compare {compareStates.length} <ArrowRight size={14} /></Link>
              )}
            </div>
          </div>
          <div className="relative min-h-[240px] lg:min-h-[440px]">
            <img src={heroImg} alt={`${state} landscape`} className="absolute inset-0 w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-r from-paper via-paper/25 to-transparent hidden lg:block" />
            <div className="absolute inset-0 bg-gradient-to-t from-paper/50 to-transparent lg:hidden" />
          </div>
        </div>
      </section>

      {/* HomeRange Verified — transparent data-health / trust panel */}
      <div className="pt-2">
        <DataHealthPanel state={state} />
      </div>

      {/* §11–12: special draws, raffles & once-in-a-lifetime rules for this state */}
      <div className="px-5 sm:px-10 max-w-[1400px] mx-auto pt-8">
        <SpecialDrawsPanel state={state} />
      </div>

      {!data ? (
        <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div>
      ) : (
        <main className="px-5 sm:px-10 pb-16 max-w-[1400px] mx-auto space-y-14">
          {/* Species rail */}
          {railNames.length > 0 && (
            <section data-testid="species-rail">
              <div className="flex items-end justify-between gap-4">
                <h2 className="font-serif text-3xl sm:text-4xl text-charcoal tracking-tight">Explore {displayState} by Species</h2>
                <Link to="/explore" className="text-sm font-semibold text-blaze hover:underline whitespace-nowrap inline-flex items-center gap-1" data-testid="view-all-species">View all species <ChevronRight size={14} /></Link>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-3 mt-5 -mx-1 px-1 hr-scroll">
                {railNames.map((name) => {
                  const dc = dataByName[name];
                  return (
                    <button key={name} onClick={() => pickSpecies(name)}
                      className="group shrink-0 w-[132px] h-64 relative overflow-hidden rounded-md transition-transform hover:-translate-y-1" data-testid={`state-species-${name}`}>
                      <img src={SPECIES_PHOTO[name] || SPECIES_PHOTO_FALLBACK} alt={name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                      <div className="absolute inset-0 bg-gradient-to-t from-charcoal/90 via-charcoal/20 to-transparent" />
                      <div className="absolute inset-x-0 bottom-0 p-3">
                        <span className="block font-serif text-xl text-white leading-tight drop-shadow">{name}</span>
                        {dc && <span className="text-[11px] text-white/70">{dc.hunt_count} hunt{dc.hunt_count === 1 ? "" : "s"}</span>}
                      </div>
                    </button>
                  );
                })}
                <Link to="/explore" className="shrink-0 w-[132px] h-64 relative overflow-hidden rounded-md bg-charcoal grid place-items-center transition-transform hover:-translate-y-1" data-testid="more-species">
                  <span className="font-serif text-lg text-white leading-tight text-center px-3">More<br />Species <ChevronRight size={15} className="inline" /></span>
                </Link>
              </div>
            </section>
          )}

          {/* Official resources */}
          {resources.length > 0 && (
            <section data-testid="official-resources">
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="font-serif text-3xl sm:text-4xl text-charcoal tracking-tight">Official {displayState} Resources</h2>
                <SourceBadge tier="official" />
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                {resources.map((r) => {
                  const m = resMeta(r.resource_type);
                  const Icon = m.Icon;
                  return (
                    <a key={r.id || r.url} href={r.url} target="_blank" rel="noreferrer"
                      className="group relative bg-white border border-saddle/20 p-4 hover:border-forest transition-colors overflow-hidden" data-testid={`resource-${r.resource_type}`}>
                      {m.photo && <div className="absolute inset-0 opacity-90"><img src={SPECIES_PHOTO_FALLBACK} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-charcoal/55" /></div>}
                      <div className="relative flex items-start gap-3">
                        {r.resource_type === "agency"
                          ? <span className="shrink-0 w-9 h-9 rounded-full border-2 border-blaze/70 grid place-items-center text-blaze"><Landmark size={16} /></span>
                          : <Icon size={20} className={`shrink-0 mt-0.5 ${m.photo ? "text-white" : "text-forest/75"}`} />}
                        <div className="min-w-0">
                          <p className={`font-serif text-[15px] leading-snug flex items-start gap-1 ${m.photo ? "text-white" : "text-charcoal"}`}>
                            {r.resource_label || m.label}
                            <ExternalLink size={12} className={`mt-1 shrink-0 ${m.photo ? "text-white/80" : "text-saddle group-hover:text-blaze"}`} />
                          </p>
                          <p className={`text-xs mt-1 ${m.photo ? "text-white/80" : "text-charcoal/55"}`}>{r.agency_name}</p>
                        </div>
                      </div>
                      <p className={`relative text-[11px] mt-3 ${m.photo ? "text-white/70" : "text-charcoal/45"}`}>Verified {fmtDate(r.last_checked_at) || "—"}</p>
                    </a>
                  );
                })}
              </div>
            </section>
          )}

          {/* Source documents & references (editor-managed) */}
          {stateDocs.length > 0 && (
            <section data-testid="state-source-docs">
              <h2 className="font-serif text-3xl sm:text-4xl text-charcoal tracking-tight">Source documents & references</h2>
              <p className="text-charcoal/55 mt-2 max-w-2xl text-sm">Official PDFs and links behind {state}&apos;s draw odds, point creep and regulations. Always confirm details with the issuing agency.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
                {stateDocs.map((d) => (
                  <a key={d.id} href={docSrc(d.url)} target="_blank" rel="noreferrer"
                    className="group bg-white border border-saddle/20 p-4 hover:border-forest transition-colors" data-testid={`state-doc-${d.category}`}>
                    <div className="flex items-start gap-3">
                      <span className="shrink-0 w-9 h-9 rounded-full border border-saddle/30 grid place-items-center text-saddle">
                        {d.doc_type === "pdf" ? <FileText size={16} /> : <ExternalLink size={16} />}
                      </span>
                      <div className="min-w-0">
                        <p className="font-serif text-[15px] leading-snug text-charcoal flex items-start gap-1">{d.title}<ExternalLink size={12} className="mt-1 shrink-0 text-saddle group-hover:text-blaze" /></p>
                        <p className="text-[11px] uppercase tracking-wide text-blaze mt-1">{DOC_CAT[d.category] || d.category}</p>
                        <p className="text-xs text-charcoal/55 mt-1">{[d.source, d.source_date].filter(Boolean).join(" · ") || "Source"}</p>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Build a hunt */}
          <section data-testid="build-hunt">
            <h2 className="font-serif text-3xl sm:text-4xl text-charcoal tracking-tight">Build your {displayState} hunt</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-2 mt-5">
              <FilterSeg icon={Compass} label="Species" value={f.species} onChange={(v) => setF({ ...f, species: v, weapon: "" })} placeholder="Choose species" options={railNames} testid="bh-species" />
              <FilterSeg icon={Crosshair} label="Weapon" value={f.weapon} onChange={(v) => setF({ ...f, weapon: v })} placeholder="Any weapon" options={weapons} testid="bh-weapon" />
              <FilterSeg icon={User} label="Residency" value={f.residency} onChange={(v) => setF({ ...f, residency: v })} options={["Resident", "Nonresident"]} testid="bh-residency" />
              <FilterSeg icon={MapIcon} label="Unit" value={f.unit} onChange={(v) => setF({ ...f, unit: v })} placeholder="Select unit" options={units} testid="bh-unit" />
              <FilterSeg icon={CalendarDays} label="Season" value={f.season} onChange={(v) => setF({ ...f, season: v })} placeholder="Any season" options={seasons} testid="bh-season" />
              <FilterSeg icon={Star} label="Points" value={f.points} onChange={(v) => setF({ ...f, points: v })} placeholder="Any points" options={["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]} testid="bh-points" />
              <button onClick={() => findHunts()} className="bg-charcoal text-white font-semibold text-sm px-4 py-3 inline-flex items-center justify-center hover:bg-blaze transition-colors" data-testid="bh-find">Find Hunts</button>
            </div>
          </section>

          {/* Draw odds & point creep */}
          <section id="draw-odds" data-testid="draw-odds-panel" className="bg-white border border-saddle/20 p-5 sm:p-7">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-serif text-3xl sm:text-4xl text-charcoal tracking-tight">Draw Odds &amp; Point Creep</h2>
                <p className="text-sm text-charcoal/60 mt-1.5">Compare tag odds and historical point creep for {state} hunts.</p>
              </div>
              <button onClick={() => setOddsInfo(true)} className="text-sm font-semibold text-blaze hover:underline whitespace-nowrap inline-flex items-center gap-1" data-testid="how-odds">How odds are calculated <ChevronRight size={14} /></button>
            </div>

            {hunts.length === 0 ? (
              <div className="mt-6 border border-dashed border-saddle/30 bg-paper p-8 text-center" data-testid="state-no-hunts">
                <p className="text-charcoal/70">Verified {state} draw data is being collected for this selection.</p>
                {data.official_source && <a href={data.official_source} target="_blank" rel="noreferrer" className="text-blaze font-semibold text-sm mt-2 inline-flex items-center gap-1">View the official {agency || "state"} source <ExternalLink size={13} /></a>}
              </div>
            ) : (
              <div className="grid lg:grid-cols-[300px_1fr] gap-5 mt-6">
                {/* Popular hunts */}
                <div className="border border-saddle/20" data-testid="popular-hunts">
                  <p className="font-serif text-lg text-charcoal px-4 py-3 border-b border-saddle/15">Popular {state} Hunts</p>
                  <div className="divide-y divide-saddle/12 max-h-[440px] overflow-y-auto">
                    {hunts.map((h) => (
                      <button key={h.id} onClick={() => setSel(h)} className={`w-full text-left px-3 py-3 flex items-center gap-3 hover:bg-paper transition-colors ${sel?.id === h.id ? "bg-paper" : ""}`} data-testid={`popular-hunt-${h.id}`}>
                        <img src={SPECIES_PHOTO[h.species] || SPECIES_PHOTO_FALLBACK} alt={h.species} className="w-12 h-12 rounded object-cover shrink-0" loading="lazy" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-charcoal leading-tight">{h.species} — {HUNT_TYPE[h.draw_type] || "Draw"}</p>
                          <p className="text-xs text-charcoal/55 truncate">{h.unit}</p>
                        </div>
                        <span className="bg-charcoal text-white text-xs font-semibold tabular-nums px-2 py-1 rounded-full shrink-0">{h.draw_odds_pct}%</span>
                        <ChevronRight size={15} className="text-charcoal/40 shrink-0" />
                      </button>
                    ))}
                  </div>
                  <Link to="/explore" className="block text-center text-sm font-semibold text-blaze hover:underline px-4 py-3 border-t border-saddle/15" data-testid="view-all-hunts">View all hunts →</Link>
                </div>
                {/* Detail */}
                {sel && <DetailPanel h={sel} agency={agency} officialSource={data.official_source} verifiedDate={verifiedDate} />}
              </div>
            )}
          </section>
        </main>
      )}

      {oddsInfo && (
        <div className="fixed inset-0 z-[70] bg-charcoal/60 grid place-items-center p-4" onClick={() => setOddsInfo(false)} data-testid="odds-info-modal">
          <div className="bg-paper max-w-lg w-full p-7 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setOddsInfo(false)} className="absolute top-4 right-4 text-charcoal/50 hover:text-charcoal" data-testid="odds-info-close"><X size={18} /></button>
            <h3 className="font-serif text-2xl text-charcoal">How {state} odds are calculated</h3>
            <p className="text-sm text-charcoal/70 mt-3 leading-relaxed">
              {state} uses a <span className="font-semibold text-forest">{data?.system?.system_label || "state draw"}</span> system. {data?.system?.system_desc}
            </p>
            <p className="text-sm text-charcoal/70 mt-3 leading-relaxed">
              Draw odds and point creep shown here come from verified {agency || "state agency"} records. Where a value has not yet been verified we show a dash (—) rather than an estimate. Personalized odds are a transparent model based on your point balance and published data — an estimate, not a guarantee.
            </p>
            {data?.official_source && <a href={data.official_source} target="_blank" rel="noreferrer" className="text-blaze font-semibold text-sm mt-4 inline-flex items-center gap-1">View the official source <ExternalLink size={13} /></a>}
          </div>
        </div>
      )}

      <StatePartnerContent state={state} />

      <div className="max-w-[1240px] mx-auto px-5 sm:px-10 pb-4">
        <PartnerRecommendations state={state} />
      </div>

      <section className="max-w-[1240px] mx-auto px-5 sm:px-10 pb-14" data-testid="become-partner-banner">
        <div className="relative overflow-hidden rounded-xl bg-forest text-bone px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="absolute inset-0 opacity-40 bg-gradient-to-br from-forest-2 to-charcoal" />
          <div className="relative flex-1">
            <span className="hr-overline text-blaze">HomeRange Partners</span>
            <h2 className="font-serif text-2xl sm:text-3xl mt-1 leading-tight">{`Outfitter, guide or agency in ${displayState}?`}</h2>
            <p className="text-bone/70 mt-2 max-w-xl text-sm">Claim your HomeRange profile to reach hunters planning trips in {state} — with a verified page, QR marketing tools, referral tracking and analytics.</p>
          </div>
          <button onClick={() => navigate("/partner/onboarding")} className="relative inline-flex items-center gap-2 bg-blaze text-white px-6 py-3.5 rounded-full font-semibold hover:bg-blaze-dark transition-colors shrink-0" data-testid="become-partner-cta">
            Become a Partner <ChevronRight size={16} />
          </button>
        </div>
      </section>

      <PolicyBar />
      <Footer />
    </div>
  );
}

function FilterSeg({ icon: Icon, label, value, onChange, placeholder, options, testid }) {
  return (
    <div className="flex items-center gap-2 bg-white border border-saddle/25 px-3 py-2">
      <Icon size={16} className="text-forest/70 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[9px] uppercase tracking-[0.12em] text-charcoal/45">{label}</p>
        <div className="relative">
          <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full appearance-none bg-transparent text-sm text-charcoal font-medium outline-none pr-4 truncate cursor-pointer" data-testid={testid}>
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((o) => <option key={o} value={o}>{label === "Points" ? `${o} pts` : o}</option>)}
          </select>
          <ChevronDown size={13} className="absolute right-0 top-1/2 -translate-y-1/2 text-charcoal/40 pointer-events-none" />
        </div>
      </div>
    </div>
  );
}

const Metric = ({ label, value, accent, testid }) => (
  <div data-testid={testid}>
    <p className="text-[10px] uppercase tracking-[0.12em] text-charcoal/45">{label}</p>
    <p className={`font-serif leading-none tabular-nums mt-1.5 ${accent ? "text-blaze text-3xl" : "text-charcoal text-3xl"}`}>{value ?? "—"}</p>
  </div>
);

function DetailPanel({ h, agency, officialSource, verifiedDate }) {
  const hist = h.odds_history || [];
  const year = h.season_year;
  const yl = year ? ` (${year})` : "";
  const source = h.source_url || officialSource;
  return (
    <div data-testid="hunt-panel">
      {/* Header */}
      <div className="bg-white border border-saddle/20 p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-serif text-2xl sm:text-3xl text-charcoal leading-tight">{h.species} — {HUNT_TYPE[h.draw_type] || "Draw"}</h3>
            <p className="text-sm text-charcoal/60 mt-0.5">{h.unit}</p>
          </div>
          {verifiedDate && <span className="inline-flex items-center gap-1.5 text-xs text-charcoal/55 shrink-0"><ShieldCheck size={13} className="text-[#2E7D46]" /> Verified {verifiedDate}</span>}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-5 pt-5 border-t border-saddle/12">
          <Metric label={`Draw odds${yl}`} value={`${h.draw_odds_pct}%`} accent testid="panel-odds" />
          <Metric label={`Avg points${yl}`} value={h.avg_points != null ? h.avg_points : "—"} testid="panel-avg" />
          <Metric label="Point creep (5 yr)" value={h.point_creep && h.point_creep !== "n/a" ? h.point_creep.replace(" pts/yr", "") : "—"} testid="panel-creep" />
          <Metric label={`Min points${yl}`} value={h.min_points != null ? h.min_points : "—"} testid="panel-min" />
          <Metric label={`Applicants${yl}`} value={h.applicants != null ? h.applicants.toLocaleString() : "—"} testid="panel-applicants" />
        </div>

        {h.personalized && (
          <div className="mt-5 bg-forest text-bone px-4 py-3 flex items-center justify-between" data-testid="panel-personal">
            <span className="hr-overline text-blaze">Your estimated odds · {h.personalized.points_used} pts</span>
            <span className="font-serif text-2xl tabular-nums">{h.personalized.est_odds_pct}%</span>
          </div>
        )}
      </div>

      {/* Chart + Unit info + Source */}
      <div className="grid md:grid-cols-[1.5fr_1fr_1fr] gap-3 mt-3">
        <div className="bg-white border border-saddle/20 p-5">
          <p className="font-serif text-lg text-charcoal">Historical Draw Odds</p>
          {hist.length ? (
            <div className="h-40 mt-2 -ml-2" data-testid="panel-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={hist} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                  <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#6b6b6b" }} axisLine={{ stroke: "#e2ddd2" }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#6b6b6b" }} axisLine={false} tickLine={false} width={34} unit="%" domain={[0, (max) => Math.max(5, Math.ceil(max * 1.2))]} />
                  <Tooltip formatter={(v) => [`${v}%`, "Draw odds"]} contentStyle={{ fontSize: 12, borderRadius: 2, border: "1px solid #e2ddd2" }} />
                  <Line type="monotone" dataKey="pct" stroke="#D95D39" strokeWidth={2.5} dot={{ r: 3, fill: "#D95D39" }} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : <p className="text-sm text-charcoal/55 mt-3">History being collected.</p>}
        </div>

        <div className="bg-white border border-saddle/20 p-5" data-testid="unit-season-info">
          <p className="font-serif text-lg text-charcoal">Unit &amp; Season Info</p>
          <dl className="mt-3 space-y-2.5 text-sm">
            <Row k="Season" v={h.season} />
            <Row k="Hunt Type" v={HUNT_TYPE[h.draw_type] || "Draw"} />
            <Row k="Weapon" v={h.weapon} />
            <Row k="Residency" v={h.residency} />
          </dl>
          <a href={`/marketplace?${new URLSearchParams({ species: h.species || "", weapon: h.weapon || "", hunt: `${h.jurisdiction} ${h.species}` }).toString()}`}
            className="text-sm font-semibold text-blaze hover:underline mt-4 inline-flex items-center gap-1" data-testid="panel-gear-up">Gear up for this hunt →</a>
        </div>

        <div className="bg-white border border-saddle/20 p-5 flex flex-col items-center text-center" data-testid="official-source-card">
          <p className="font-serif text-lg text-charcoal self-start">Official Source</p>
          <span className="mt-4 w-16 h-16 rounded-full border-2 border-blaze/70 grid place-items-center text-blaze"><Landmark size={26} /></span>
          <p className="text-sm font-medium text-charcoal mt-3 leading-tight">{agency || "State wildlife agency"}</p>
          {source && <a href={source} target="_blank" rel="noreferrer" className="text-sm font-semibold text-blaze hover:underline mt-3 inline-flex items-center gap-1" data-testid="panel-source">View official site <ExternalLink size={13} /></a>}
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex items-start justify-between gap-3">
    <dt className="text-charcoal/55 shrink-0">{k}</dt>
    <dd className="text-charcoal font-medium text-right">{v || "—"}</dd>
  </div>
);
